import { cn } from "@/libs/utils";
import { GameResell } from "@/models/GameResell";
import { gamePortraitImage } from "@/libs/games/game-square-image";
import { BhapticsContent } from "@/store/reducers/contents";
import BhapticsContentCard from "@/v2-components/BhapticsContentCard";
import React, { Fragment } from "react";
import { Badge } from "@/v2-components/ui/badge";
import MetaBlueIcon from "@/v2-components/core/icon/meta-icon-blue";
import SteamBlueIcon from "@/v2-components/core/icon/steam-icon-blue";
import { useTrans } from "@/utils/useTrans";

/**
 * 플랫폼 하나의 키 재고 — `platformKeyStock` 결과 그대로(`true` 있음 · `false` 품절 · `null` 모름).
 * 장바구니 · 서버 결제 지점과 **같은 술어**다(`docs/cart-game-key-stock-policy.md` §1.3).
 */
export type PlatformKeyStock = boolean | null;

/**
 * 게임 키 목록 (피그마 28769:63667 `Lists`).
 *
 * 제목과 "Please select a game key" 보조줄은 **호출처 AccordionTrigger** 가 갖는다
 * (`ProductGameKeys`) — 접힌 상태에서도 미선택 경고가 보여야 하기 때문.
 *
 * ★**재고가 확인된 플랫폼만 고를 수 있다**(#6136). 품절은 「Sold Out」, 모름은 「Checking stock…」으로 카드를 흐리게 하고
 * 클릭을 막는다 — 장바구니 밖 구매 표면은 모르는 재고를 팔지 않는다(정책 §1.6, 게임 상세 키 선택 다이얼로그와 같은 문구).
 * 종전엔 플랫폼을 전부 고를 수 있어, 재고 없는 플랫폼을 담은 고객이 장바구니에서야 품절을 알았다(서버는 409 로 막았다).
 * 시안(`31633:38985` S1–S5)엔 품절 상태가 없어 기존 문구 · 흐림 처리를 따랐다.
 */
export function GameKeySelect({
  games,
  onSelect,
  selectedGameResells,
  stockOf,
}: {
  games: (BhapticsContent & { gameResell: GameResell })[];
  selectedGameResells: { id: string; platform: string }[];
  onSelect: (option: { id: string; platform: string }) => void;
  stockOf: (gameResellId: string, platform: string) => PlatformKeyStock;
}) {
  // 에러 배경은 여기가 아니라 **섹션(AccordionItem)** 이 갖는다 — 피그마
  // 27471:53956 에서 핑크가 제목·경고문까지 감싼다. 목록에만 걸면 헤더가 밖에 남는다.
  return (
    <div className="flex flex-col gap-y-3">
      {games.map((game, index) => {
        const gameResellOption = selectedGameResells.find(
          (s) => s.id === game.gameResell.id,
        ) || { id: "", platform: "" };
        return (
          <SelectPlatform
            selectedOption={gameResellOption}
            onSelect={onSelect}
            stockOf={stockOf}
            game={game}
            key={index}
          />
        );
      })}
    </div>
  );
}

const SelectPlatform = ({
  game,
  selectedOption,
  onSelect,
  stockOf,
}: {
  game: BhapticsContent & {
    gameResell: GameResell;
  };
  selectedOption: { id: string; platform: string };
  onSelect: (option: { id: string; platform: string }) => void;
  stockOf: (gameResellId: string, platform: string) => PlatformKeyStock;
}) => {
  const { t } = useTrans(["gnb"]);
  const { gameResell } = game;
  return (
    <Fragment>
      {gameResell.platformList.map((item, index) => {
        const stock = stockOf(gameResell.id, item.platform);
        const selectable = stock === true;
        const selected =
          selectable && selectedOption.platform === item.platform;
        return (
          <div
            aria-disabled={!selectable || undefined}
            className={cn(
              selectable ? "cursor-pointer" : "cursor-not-allowed opacity-50",
              // ⚠️ 기존 `outline-blue-500` 은 **Tailwind 기본 파랑(#3B82F6)** 이었다.
              // 이 프로젝트의 blue 팔레트는 숫자 10~100 을 extend 로 얹은 것이라
              // `blue-500` 이 지워지지 않고 남아 조용히 다른 파랑으로 칠해졌다.
              // 브랜드 파랑은 `blue-60`(#0F62FE).
              selected &&
                "rounded-lg outline outline-2 -outline-offset-2 outline-blue-60",
            )}
            onClick={() => {
              if (!selectable) return;
              onSelect({
                id: gameResell.id,
                platform: item.platform,
              });
            }}
            key={index}
          >
            <BhapticsContentCard bhapticsContent={game}>
              <div className="rounded-lg border border-coolgray-20 bg-white p-4">
                <div className="flex items-center gap-x-4">
                  {/* 게임 아트 55×80, radius 4 (28769:63669).
                      번들에 든 게임 키는 mgmt 게임 리셀의 세로 이미지를 먼저 쓴다(EXOSHOCK = 이 칸용 688×1000) — `gamePortraitImage`.
                      없으면 종전 콘텐츠 세로 썸네일 */}
                  <div className="relative aspect-[55/80] w-full max-w-[55px] shrink-0 overflow-hidden rounded">
                    <BhapticsContentCard.Thumbnail
                      fill
                      src={gamePortraitImage(gameResell)}
                      className={
                        gamePortraitImage(gameResell)
                          ? "object-cover"
                          : undefined
                      }
                    />
                  </div>
                  <div className="flex flex-col justify-center gap-y-2">
                    <div className="flex items-center gap-x-2">
                      {item.platform === "quest" && (
                        <MetaBlueIcon className="!size-6" />
                      )}
                      {item.platform === "steam" && (
                        <SteamBlueIcon className="!size-6" />
                      )}
                      <BhapticsContentCard.Name className="text-lg font-600 tracking-[0.5px]" />
                    </div>
                    <div className="flex items-center gap-1">
                      {!selectable && (
                        <span className="text-xs font-500 tracking-[0.5px] text-coolgray-60">
                          {stock === false
                            ? t("Sold Out")
                            : t("Checking stock")}
                        </span>
                      )}
                      {/* KeySaleBadge — blue-10 배경 / blue-60 글자, 11px/16 w500 ls.5,
                          h20 px4 (28769:63675). Badge 기본 variant `new` 는 글자색이
                          blue-hover-70 이고 size md 는 14px 이라 둘 다 덮는다. */}
                      {selectable && (
                        <Badge
                          variant={"new"}
                          className="h-5 px-1 text-xs font-500 tracking-[0.5px] text-blue-60"
                        >
                          Bundle Item
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </BhapticsContentCard>
          </div>
        );
      })}
    </Fragment>
  );
};
